import { CHIEF_COMPLAINT_MAX_LENGTH } from '@/src/constants'
import { useAppSelector } from '@/src/store/hooks'
import { icdsAction } from '@api'
import { useEncounter, useIntake, useMedical } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { medicalSchema } from '@schema/pDirectory.schema'
import { MedDataMock } from '@type/history.type'
import { IcdDataMock } from '@type/icd.type'
import { FormButton, Modal, SelectInputField, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { flattenKeysAndValuesUnique, getError, showToast } from '@util'
import { Box, Form, Text } from 'grommet'
import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { useParams, useSearchParams } from 'react-router-dom'

type Props = {
  onClose: () => void
  id?: string
  defaultData?: MedDataMock
}

const EditMedical = ({ id, defaultData, onClose }: Props) => {
  const [onChangeOptions, setOnChangeOptions] = useState<IcdDataMock[]>()
  const { medical, updateMedical } = useMedical({ id })
  const status = useAppSelector(state => state.config.medical_history_transitions as Record<string, string[]>)
  const codesArray = medical?.data.relationships.icd_codes.data?.map(({ id }) => id)
  const icdsData: any = medical?.included.filter(({ type, id }) => type === 'icd_code' && codesArray?.includes(id))
  const methods = useForm<any>({
    resolver: yupResolver(medicalSchema),
    defaultValues: { ...defaultData?.attributes, status: defaultData?.meta.status }
  })
  const [searchParams] = useSearchParams()
  const { patientId, encounterId, intakeId: paramsIntakeId } = useParams<string>()

  const isEncounterProblemTab = searchParams.get('encounterTab') === 'Encounter Problems'

  const { singleEncounter } = useEncounter({ id: encounterId })
  const intakeId = singleEncounter?.relationships.intake.data.id

  const { intake, updateIntake } = useIntake({ id: intakeId })

  const chiefComplaint = intake?.data.attributes.chief_complaint

  useEffect(() => {
    if (isEncounterProblemTab && chiefComplaint && !methods.getValues('comment')) {
      methods.setValue('comment', chiefComplaint)
    }
  }, [chiefComplaint, isEncounterProblemTab])

  const commentValue: string = useWatch({ control: methods.control, name: 'comment' }) || ''

  const onSubmit = async (data: any) => {
    const payload = {
      data: {
        attributes: {
          ...data
        },
        type: 'medical_history'
      }
    }
    try {
      const requests: Promise<any>[] = [updateMedical({ formVal: payload, id: id || '' })]
      if (isEncounterProblemTab && intakeId && data.comment && data.comment !== chiefComplaint) {
        requests.push(
          updateIntake({
            formVal: {
              data: {
                attributes: {
                  chief_complaint: data.comment
                },
                id: intakeId,
                type: 'intake'
              }
            },
            id: intakeId
          })
        )
      }

      await Promise.all(requests)
      showToast('Medical updated successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }
  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title={isEncounterProblemTab ? 'Edit Encounter Problem' : 'Edit Medical'}>
        <Form
          onSubmit={methods.handleSubmit(onSubmit)}
          style={{ display: 'flex', flexDirection: 'column', overflow: 'auto' }}
        >
          <Box
            // flex
            overflow={'auto'}
            pad="medium"
            style={{ display: 'block', scrollbarColor: '#fff', scrollbarWidth: 'thin', width: '50 em' }}
          >
            <Box gap={'24px'}>
              <Box>
                <TextAreaField
                  label={isEncounterProblemTab ? 'Clinical Presentation (Chief Complaint)' : 'Comments'}
                  maxLength={CHIEF_COMPLAINT_MAX_LENGTH}
                  name="comment"
                  placeholder="Enter Note"
                  style={{ borderRadius: '14px', minHeight: '100px' }}
                />
                {isEncounterProblemTab && (
                  <Box direction="row" justify="between" margin={{ top: 'xsmall' }}>
                    <Text color="dark-4" size="xsmall">
                      Pre-filled from Chief Complaint. Edit as needed for this problem.
                    </Text>
                    <Text color="dark-4" size="xsmall">
                      {commentValue.length}/{CHIEF_COMPLAINT_MAX_LENGTH}
                    </Text>
                  </Box>
                )}
              </Box>

              <Box>
                <Controller
                  name="icd_codings_attributes"
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <Select
                      cacheOptions
                      defaultOptions
                      error={error?.message}
                      getOptionLabel={({ attributes }) => `${attributes.title} - ${attributes.code}`}
                      getOptionValue={({ id }) => id}
                      isAsync
                      isMulti
                      label={isEncounterProblemTab ? 'Coding (Diagnosis / Problem Code)' : 'Coding'}
                      loadOptions={icdsAction}
                      onChange={selectedOptions => {
                        const newVal = selectedOptions
                        setOnChangeOptions([...newVal])
                        const values = selectedOptions
                          ? selectedOptions?.map((option: any) => ({
                              icd_code_id: option?.id // Map the selected value
                            }))
                          : []

                        onChange(values) // Update the form value
                      }}
                      value={onChangeOptions || icdsData}
                    />
                  )}
                />
                {isEncounterProblemTab && (
                  <Text color="dark-4" margin={{ top: 'xsmall' }} size="xsmall">
                    Select the most appropriate code that matches the clinical presentation.
                  </Text>
                )}
              </Box>

              <Box>
                <SelectInputField label="Status" name="status" options={flattenKeysAndValuesUnique(status || {})} />
                {isEncounterProblemTab && (
                  <Text color="dark-4" margin={{ top: 'xsmall' }} size="xsmall">
                    Select the current status of this problem.
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
          <FormButton isLoading={methods.formState.isSubmitting} onClose={onClose} submit="Save" />
        </Form>
      </Modal>
    </FormProvider>
  )
}

export default EditMedical
